import { useEffect, useState } from "react";
import { OutcomeBoard } from "../components/OutcomeBoard";
import { Link } from "react-router-dom";
import { ShieldCheck, Search, Download, FileText, AlertTriangle, TrendingUp, Users, Home, Sprout, Heart, Zap } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { FadeIn } from "../components/motion";

const NGN = (n) => `₦${Number(n || 0).toLocaleString()}`;
const NUM = (n) => Number(n || 0).toLocaleString();

const PROGRAMME_LABELS = {
  empowerment: "Empowerment", family_food: "Family food", shelter_housing: "Shelter & housing",
  education: "Education", health: "Health", water_sanitation: "Water & sanitation",
  energy: "Energy & electrification", agriculture: "Agriculture & livelihoods",
  emergency_relief: "Emergency relief", community_development: "Community development",
};

const POLICIES = [
  "Foundation Constitution", "Governance Policies", "Safeguarding Policy",
  "Conflict of Interest Policy", "Donation Policy", "Beneficiary Selection Policy",
  "Political Neutrality Policy", "Procurement Policy", "Data Protection Policy",
  "Complaints Policy", "Whistleblowing Policy",
];

export default function Transparency() {
  const [dash, setDash] = useState(null);
  const [ref, setRef] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    document.title = "Impact & Transparency Dashboard | Dutsen Kauna Foundation";
    api.get("/impact-dashboard").then((r) => setDash(r.data)).catch(() => {});
  }, []);

  const verify = async (e) => {
    e.preventDefault();
    if (!ref) return;
    setVerifying(true); setVerifyResult(null);
    try {
      const { data } = await api.get(`/verify-reference?serial=${encodeURIComponent(ref)}`);
      setVerifyResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setVerifying(false); }
  };

  const g = dash?.at_a_glance || {};
  const apps = dash?.applications || {};

  return (
    <div data-testid="transparency-page">
      {/* Hero */}
      <section className="relative bg-[#155e3f] text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] to-[#155e3f]" />
        <div className="dk-container relative">
          <span className="text-[#d6aa43] tracking-[0.35em] text-xs font-semibold uppercase">Impact & Transparency</span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold mt-3 max-w-4xl leading-[1.05]">
            See the work. <span className="italic text-[#d6aa43]">Follow the funds.</span> Measure the impact.
          </h1>
          <p className="mt-6 text-white text-lg max-w-2xl">
            Receive responsibly. Spend transparently. Implement carefully. Report truthfully. Measure long-term impact.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/projects" className="dk-btn-primary">View active projects</Link>
            <Link to="/donate" className="dk-btn-outline">Donate</Link>
          </div>
        </div>
      </section>

      {/* At a glance */}
      <section className="py-16 bg-white">
        <div className="dk-container">
          <FadeIn>
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Foundation impact at a glance</span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0a0d12] font-bold mt-3 mb-8">Live figures updated as programmes run.</h2>
          </FadeIn>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dash-glance">
            {[
              ["Total donations received", NGN(g.total_donations_ngn), TrendingUp],
              ["Beneficiaries registered", NUM(g.beneficiaries_total), Users],
              ["Families supported", NUM(g.families_supported), Heart],
              ["Individuals empowered", NUM(g.individuals_empowered), TrendingUp],
              ["Active projects", NUM(g.active_projects), Sprout],
              ["Completed projects", NUM(g.completed_projects), ShieldCheck],
              ["Training participants", NUM(g.training_participants), Zap],
              ["Next empowerment selection", g.next_empowerment_selection || "TBA", ShieldCheck],
            ].map(([l, v, Icon]) => (
              <div key={l} className="p-5 rounded-xl bg-[#f8f4e8] border border-black/5">
                <Icon size={16} className="text-[#d6aa43] mb-2" />
                <div className="font-serif text-2xl md:text-3xl text-[#0a0d12] font-bold">{v}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#0d1724]/60 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programme spend */}
      <section className="py-16 bg-[#f8f4e8]">
        <div className="dk-container">
          <FadeIn>
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Where our support goes</span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0a0d12] font-bold mt-3 mb-8">Programme funding deployed.</h2>
          </FadeIn>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(dash?.programme_spend_ngn || {}).map(([k, v]) => (
              <div key={k} className="p-4 rounded-xl bg-white border border-black/5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold">{PROGRAMME_LABELS[k] || k}</div>
                <div className="font-serif text-xl text-[#0a0d12] font-bold mt-1">{NGN(v)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications breakdown */}
      <section className="py-16 bg-white">
        <div className="dk-container grid lg:grid-cols-2 gap-8">
          <div>
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Application statistics</span>
            <h2 className="font-serif text-3xl text-[#0a0d12] font-bold mt-3 mb-6">By category</h2>
            <div className="space-y-2" data-testid="dash-apps-cat">
              {Object.entries(apps.by_category || {}).slice(0, 10).map(([k, v]) => (
                <div key={k} className="flex items-center gap-3">
                  <div className="text-sm flex-1 text-[#0a0d12] capitalize">{k.replace(/_/g, " ")}</div>
                  <div className="flex-1 h-2 rounded-full bg-[#f8f4e8] overflow-hidden">
                    <div className="h-full bg-[#d6aa43]" style={{ width: `${Math.min(100, (v / Math.max(1, apps.total)) * 100)}%` }} />
                  </div>
                  <div className="text-sm font-bold text-[#0a0d12] w-8 text-right">{v}</div>
                </div>
              ))}
              {(!apps.by_category || Object.keys(apps.by_category).length === 0) && <p className="text-sm text-[#0a0d12]/60">No applications yet.</p>}
            </div>
          </div>
          <div>
            <h2 className="font-serif text-3xl text-[#0a0d12] font-bold mt-9 mb-6">By status</h2>
            <div className="space-y-2" data-testid="dash-apps-status">
              {Object.entries(apps.by_status || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between p-3 rounded-md bg-[#f8f4e8] border border-black/5">
                  <span className="text-sm capitalize text-[#0a0d12]">{k.replace(/_/g, " ")}</span>
                  <span className="font-bold text-[#0d1724]">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reference verification */}
      <section className="py-16 bg-[#155e3f] text-white">
        <div className="dk-container max-w-3xl">
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Public reference verification</span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold mt-3 mb-6">Verify any DKF reference.</h2>
          <p className="text-white mb-6">Enter any DKF-… serial number to confirm it is genuine. Only safe information is displayed — no private beneficiary data.</p>
          <form onSubmit={verify} className="flex flex-col sm:flex-row gap-2" data-testid="verify-form">
            <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. DKF-APP-2026-001524" className="flex-1 px-4 py-3 rounded-md bg-white/10 border border-white/15 text-white font-mono placeholder:text-white/90 focus:outline-none focus:border-[#d6aa43]" data-testid="verify-input" />
            <button type="submit" disabled={verifying} className="dk-btn-primary" data-testid="verify-btn">
              {verifying ? "Checking…" : <><Search size={14} /> Verify</>}
            </button>
          </form>
          {verifyResult && (
            <div className={`mt-4 p-5 rounded-xl ${verifyResult.valid ? "bg-white/5 border border-[#d6aa43]/40" : "bg-red-500/10 border border-red-500/40"}`} data-testid="verify-result">
              {verifyResult.valid ? (
                <>
                  <div className="text-[#d6aa43] font-bold mb-2">✓ Reference valid</div>
                  <div className="grid sm:grid-cols-3 gap-3 text-sm">
                    <div><div className="text-[10px] uppercase tracking-[0.2em] text-white">Type</div><div className="font-semibold capitalize">{verifyResult.type?.replace(/_/g, " ")}</div></div>
                    <div><div className="text-[10px] uppercase tracking-[0.2em] text-white">Programme</div><div className="font-semibold capitalize">{verifyResult.programme}</div></div>
                    <div><div className="text-[10px] uppercase tracking-[0.2em] text-white">Status</div><div className="font-semibold capitalize">{verifyResult.status}</div></div>
                  </div>
                </>
              ) : (
                <div className="text-red-300">{verifyResult.message}</div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Reports + Policies + Concern */}
      <section className="py-16 bg-white">
        <div className="dk-container grid md:grid-cols-3 gap-6">
          <div className="p-6 rounded-2xl border border-black/5 bg-[#f8f4e8]">
            <FileText className="text-[#d6aa43] mb-3" />
            <h3 className="font-serif text-xl font-bold text-[#0a0d12] mb-3">Financial reports</h3>
            <ul className="space-y-1 text-sm">
              {["Monthly Programme Summary", "Quarterly Impact Report", "Annual Report", "Audited Accounts (when available)"].map((r) => (
                <li key={r} className="flex items-center justify-between p-2 rounded-md hover:bg-white">
                  <span className="text-[#0a0d12]">{r}</span>
                  <button className="text-[#d6aa43] hover:text-[#0d1724]"><Download size={14} /></button>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-6 rounded-2xl border border-black/5 bg-[#f8f4e8]">
            <ShieldCheck className="text-[#d6aa43] mb-3" />
            <h3 className="font-serif text-xl font-bold text-[#0a0d12] mb-3">Governance & policies</h3>
            <ul className="space-y-1 text-sm text-[#0a0d12]">
              {POLICIES.map((p) => (<li key={p} className="flex items-center gap-2"><ShieldCheck size={12} className="text-[#d6aa43]" /> {p}</li>))}
            </ul>
          </div>
          <div className="p-6 rounded-2xl border-l-4 border-red-500 bg-red-50">
            <AlertTriangle className="text-red-600 mb-3" />
            <h3 className="font-serif text-xl font-bold text-[#0a0d12] mb-2">Report a concern</h3>
            <p className="text-sm text-[#0a0d12]/80 mb-3">Suspect fraud, misuse of Foundation money, false beneficiary claims, or safeguarding concerns? Report it confidentially.</p>
            <Link to="/contact" className="text-red-700 font-semibold text-sm hover:underline">Contact the Foundation →</Link>
          </div>
        </div>
      </section>

      <section id="methodology" className="py-16 bg-white" data-testid="transparency-methodology">
        <div className="dk-container grid lg:grid-cols-3 gap-8">
          <div><span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">How we count</span><h2 className="font-serif text-3xl font-bold mt-2">Interim transparency, not placeholders.</h2><p className="text-sm text-[#0a0d12]/70 mt-3">Our first full impact and annual reports will be published after the first complete reporting year. Until then we publish what is verified, how it was verified, and what is still pending.</p></div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4 text-sm">
            {[["People supported", "Counted once per unique beneficiary number (DKF-BEN), never per transaction."], ["Projects completed", "Only after a handover document and site photographs are filed against the project number."], ["Funds deployed", "Sum of reconciled DKF-PAY disbursements, not pledges or targets."], ["Outcomes & impact", "Follow-up visits at 3, 6 and 12 months; unverified figures are never published."], ["Budgets", "Shown only after community validation and a technical quotation; otherwise marked 'under verification'."], ["Stories", "Published with signed consent; names may be changed to protect dignity."]].map(([t, x]) => <div key={t} className="p-4 rounded-xl bg-[#f8f4e8] border border-[#d6aa43]/15" data-testid={`method-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}><div className="font-bold">{t}</div><p className="text-[#0a0d12]/70 mt-1 text-xs leading-relaxed">{x}</p></div>)}
          </div>
        </div>
        <div id="reports" className="dk-container mt-10 p-5 rounded-2xl bg-[#155e3f] text-white flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="transparency-reports">
          <div><div className="text-xs uppercase tracking-widest text-[#d6aa43] font-bold">Reports</div><p className="text-sm text-white mt-1">Impact report and annual report: first editions follow the first full reporting year. Meanwhile every project publishes its own trace, and completed projects carry verified impact certificates.</p></div>
          <div className="flex gap-2 flex-wrap"><Link to="/projects?status=completed" className="dk-btn-primary !py-2 text-sm">First verified interventions</Link><Link to="/impact#stories" className="dk-btn-outline !border-white/40 !text-white !py-2 text-sm">Stories</Link></div>
        </div>
      </section>

      <OutcomeBoard />

      {/* Promise */}
      <section className="py-16 bg-[#155e3f] text-white text-center">
        <div className="dk-container max-w-2xl">
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Our promise</span>
          <h2 className="font-serif text-3xl md:text-5xl font-bold mt-4">We report the money. We report the work. We protect the people.</h2>
        </div>
      </section>
    </div>
  );
}
